// Consumer hook for the channel-aware chat view.
//
// Takes a `channelId` and derives that channel's merged messages (history
// + live), loading state, and a bound `send(segments, attachments)`.
// All channels share the same underlying socket through the provider —
// switching channels in the UI is a free re-derivation, no reconnect.
//
// `loadMore` triggers the next (older) page from the infinite history query.
// It is a no-op when `hasMore` is false or a fetch is already in-flight.

import { useCallback, useContext, useMemo } from 'react';
import type { ChatStatus, CommsMessage, MessageSegment } from '../../interfaces/ICommsApi.js';
import type { IPendingAttachment } from '../../interfaces/ICommsApi.js';
import { ChatContext } from './ChatContext.js';
import { useChannelHistory } from './useChannelHistory.js';

export interface IUseChatResult {
  messages: CommsMessage[];
  status: ChatStatus;
  send: (segments: MessageSegment[], attachments: IPendingAttachment[]) => void;
  retry: (message: CommsMessage) => void;
  discard: (message: CommsMessage) => void;
  reportRead: (messageIds: string[]) => void;
  localAttachments: Record<string, IPendingAttachment[] | undefined>;
  isLoadingHistory: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  loadMore: () => void;
}

const EMPTY_LIVE: CommsMessage[] = [];

/**
 * Merge history (oldest→newest) with the live bucket, dedup by id, sort
 * chronologically. Live wins on id collision so UI-only fields (e.g.
 * `status: 'pending'`) survive the merge.
 */
function mergeMessages(historical: CommsMessage[], live: CommsMessage[]): CommsMessage[] {
  const byId = new Map<string, CommsMessage>();
  for (const message of historical) byId.set(message.id, message);
  for (const message of live) byId.set(message.id, message);
  return [...byId.values()].sort((x, y) => x.createdAt.localeCompare(y.createdAt));
}

export function useChat(channelId: string, options?: { isVirtual?: boolean }): IUseChatResult {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside <ChatSocketProvider>');

  // A virtual (not-yet-persisted) DM has no history to fetch — the backend
  // 404s a channel id it doesn't know yet. Skip the request entirely rather
  // than let it fail; the thread just renders as empty, which is correct.
  const isVirtual = options?.isVirtual ?? false;
  const historyQuery = useChannelHistory(ctx.siteId, channelId, { enabled: !isVirtual });
  const liveBucket = ctx.liveMessagesByChannel[channelId] ?? EMPTY_LIVE;

  const messages = useMemo(() => {
    // Each page is newest-first from the server; reverse each page before
    // merging so the combined array is oldest→newest.
    const historical: CommsMessage[] = [];
    if (historyQuery.data?.pages) {
      for (const page of historyQuery.data.pages) {
        historical.push(...[...page.messages].reverse());
      }
    }
    return mergeMessages(historical, liveBucket);
  }, [historyQuery.data, liveBucket]);

  const ctxSend = ctx.send;
  const send = useCallback(
    (segments: MessageSegment[], attachments: IPendingAttachment[]) => {
      ctxSend(channelId, segments, attachments);
    },
    [ctxSend, channelId],
  );

  const ctxReportRead = ctx.reportRead;
  const reportRead = useCallback(
    (messageIds: string[]) => ctxReportRead(channelId, messageIds),
    [ctxReportRead, channelId],
  );

  const loadMore = useCallback(() => {
    if (historyQuery.hasNextPage && !historyQuery.isFetchingNextPage) {
      void historyQuery.fetchNextPage();
    }
  }, [historyQuery]);

  return {
    messages,
    status: ctx.status,
    send,
    retry: ctx.retry,
    discard: ctx.discard,
    reportRead,
    localAttachments: ctx.localAttachments,
    // A disabled query stays `isPending: true` forever (it never runs) —
    // override so a virtual channel doesn't show a permanent loading state.
    isLoadingHistory: isVirtual ? false : historyQuery.isPending,
    hasMore: isVirtual ? false : (historyQuery.hasNextPage ?? false),
    isFetchingMore: isVirtual ? false : historyQuery.isFetchingNextPage,
    loadMore,
  };
}
